import SlideGallery from '@/components/app/templates/slide-gallery/SlideGallery';
import DownloadItemButton from '@/components/app/utils/download-item-button/DownloadItemButton';
import GslidesIcon from '@/components/icons/gslides';
import PptIcon from '@/components/icons/powerpoint';
import { HeartFilled, HeartOutlined } from '@ant-design/icons';
import { ArrowDropDownRounded, ArrowDropUpRounded } from '@mui/icons-material';
import { Avatar, Box, Collapse, Divider, Grid, IconButton, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { Breadcrumb, Button, Descriptions, Row, Space, Tag } from 'antd';
import axios from 'axios';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import useSWR from 'swr';
import CustomDesignWidget from '../CustomDesignWidget';
import TemplatesCard from '../template-card';
import styles from './TemplateView.module.css';

export default function TemplateView({ slug, id, ...props }) {
   const session = useSession();
   const [descriptionExpanded, setDescriptionExpanded] = useState(false);
   const [likeLoading, setLikeLoading] = useState(false);
   const template = useSWR(
      slug || id ? process.env.NEXT_PUBLIC_PREMAST_API_URL + '/product?slug=' + slug : null,
      (url) =>
         axios
            .get(url, {
               params: {
                  current_user_email: session?.data?.user?.email,
               },
            })
            .then((res) => res.data)
   );

   const relatedTemplatesString = template?.data?.related ? template?.data?.related.join(',') : '';
   const relatedTemplates = useSWR(process.env.NEXT_PUBLIC_PREMAST_API_URL + '/products', (url) =>
      axios
         .get(url, {
            params: {
               include_ids: relatedTemplatesString,
               category: 884,
               current_user_email: session?.data?.user?.email,
            },
         })
         .then((res) => res.data.data)
   );

   async function handleIsLiked() {
      setLikeLoading(true);
      await axios.post('/api/user/favourites', { item_id: template.data.id }).then(() => {
         template.mutate().then(() => setLikeLoading(false));
      });
   }

   if (template.isLoading) {
      return (
         <Stack spacing={16} direction="row">
            <Stack spacing={8} direction="column" className="template-view" width="70%">
               <Skeleton variant="rounded" height={455} width="100%" />
            </Stack>
            <Stack spacing={8} direction="column" className="template-details" width="30%">
               <Skeleton variant="text" width="90%" />
               <Stack spacing={2} direction="column">
                  <Skeleton variant="text" height={14} width="90%" />
                  <Skeleton variant="text" height={14} width="90%" />
                  <Skeleton variant="text" height={14} width="60%" />
               </Stack>
               <Skeleton variant="rounded" height={45} width="100%" />
            </Stack>
         </Stack>
      );
   }

   if (template.error) {
      return <p>Error: {template.error.message}</p>;
   }
   if (template.data) {
      return (
         <Stack spacing={16} direction="column">
            <Breadcrumb
               separator=">"
               style={{
                  fontSize: '14px',
                  fontWeight: '400',
               }}
            >
               <Breadcrumb.Item>
                  <Link href="/dashboard">Home</Link>
               </Breadcrumb.Item>
               <Breadcrumb.Item>
                  <Link href="/templates">Templates</Link>
               </Breadcrumb.Item>
               <Breadcrumb.Item>{template.data.name}</Breadcrumb.Item>
            </Breadcrumb>
            <Stack spacing={16} className={styles.container}>
               <Stack spacing={8} direction="column" className={styles.templateView} width="65%">
                  {Array.isArray(template.data.slides_gallery) ? (
                     <SlideGallery slides={template.data.slides_gallery?.map((slide) => slide.url)} />
                  ) : Array.isArray(template.data.gallery) ? (
                     <SlideGallery
                        slides={template.data.gallery?.map((slide) => slide.large)}
                        className={styles.featured_image}
                     />
                  ) : (
                     <Paper sx={{ overflow: 'hidden', height: '100%', display: 'flex' }} variant="outlined">
                        <Image
                           src={template.data.featured_image}
                           alt={template.name}
                           width={1000}
                           height={850}
                           className={styles.featured_image}
                        />
                     </Paper>
                  )}
               </Stack>
               <Stack spacing={8} direction="column" className="template-details" >
                  <Stack spacing={8} direction="column">
                     <Row key="top action bar" justify="space-between" align="middle">
                        {template.data.price > 0 && (
                           <Tag
                              color="gold"
                              style={{
                                 fontSize: '14px',
                                 fontWeight: '400',
                                 width: 'fit-content',
                                 padding: '0 8px',
                              }}
                           >
                              Premium
                           </Tag>
                        )}

                        <Button
                           icon={template.data.is_liked ? <HeartFilled /> : <HeartOutlined />}
                           shape="circle"
                           type={template.data.is_liked ? 'primary' : 'default'}
                           onClick={handleIsLiked}
                           loading={likeLoading}
                        />
                     </Row>

                     <Typography variant="h1">{template.data.name}</Typography>
                     <Stack spacing={4} direction="row">
                        <Avatar src={template.data.avatar} alt={template.data.author_information?.name} />
                        <Stack spacing={2} direction="column">
                           <Typography variant="body1">by {template.data.author_information?.name}</Typography>
                           <Typography variant="body1" fontSize={10}>
                              {template.data.author_information?.username}
                           </Typography>
                        </Stack>
                     </Stack>
                  </Stack>
                  <DownloadItemButton
                     itemId={template.data.id}
                     itemType="template"
                     itemPremium={template.data.price > 0}
                     item={template.data}
                  />

                  <CustomDesignWidget />

                  <Stack spacing={2} direction="column">
                     <Descriptions size="small" column={1} bordered title="Details">
                        <Descriptions.Item label="Combetible with">
                           <Tag
                              icon={
                                 template.data.software === 'powerpoint' ? (
                                    <PptIcon />
                                 ) : template.data.software === 'google-slides' ? (
                                    <GslidesIcon />
                                 ) : null
                              }
                              color={
                                 template.data.software === 'powerpoint'
                                    ? 'volcano'
                                    : template.data.software === 'google-slides'
                                    ? 'gold'
                                    : 'default'
                              }
                              style={{
                                 fontSize: '16px',
                                 fontWeight: '400',
                                 width: 'fit-content',
                                 padding: '0 8px',
                              }}
                           >
                              {' '}
                              {template.data.software === 'powerpoint'
                                 ? 'PowerPoint'
                                 : template.data.software === 'google-slides'
                                 ? 'Google Slides'
                                 : null}
                           </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Last updated">{template.data.last_update}</Descriptions.Item>
                        <Descriptions.Item label="Published">{template.data.published}</Descriptions.Item>
                     </Descriptions>

                     <Divider
                        variant="middle"
                        style={{
                           width: '100%',
                           lineHeight: 0,
                           margin: '32px 0',
                           fontSize: '14px',
                           fontWeight: '400',
                           color: 'rgba(0, 0, 0, 0.5)',
                        }}
                        textAlign="left"
                        flexItem
                     >
                        Tags
                     </Divider>
                     <Space spacing="small" wrap>
                        {template.data.tags.map((tag, i) => (
                           <Tag
                              key={i}
                              color="default"
                              style={{
                                 fontSize: '14px',
                                 fontWeight: '400',
                                 width: 'fit-content',
                                 padding: '0 8px',
                              }}
                           >
                              {tag.name}
                           </Tag>
                        ))}
                     </Space>
                  </Stack>
               </Stack>
            </Stack>
            <Divider />
            <Stack
               spacing={8}
               direction="row"
               alignItems={'center'}
               onClick={() => setDescriptionExpanded(!descriptionExpanded)}
            >
               <Typography variant="h2">Description</Typography>
               <IconButton onClick={() => setDescriptionExpanded(!descriptionExpanded)}>
                  {descriptionExpanded ? <ArrowDropUpRounded /> : <ArrowDropDownRounded />}
               </IconButton>
            </Stack>

            <Collapse in={descriptionExpanded} timeout="auto" unmountOnExit>
               {/* render HTML description */}
               <div dangerouslySetInnerHTML={{ __html: template.data.description }} className={styles.description} />
            </Collapse>

            <Divider />

            {/*
             *ANCHOR - Related Templates
             */}
            <Typography variant="h2">More like this</Typography>

            {relatedTemplates.isLoading && (
               <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={{ xs: 4, md: 8, lg: 16 }} columns={{ xs: 4, sm: 8, md: 12, lg: 16 }}>
                     {template.data.related.map((template) => (
                        <Grid item xs={4} sm={4} md={4} lg={4} key={template}>
                           <Paper variant="outlined" sx={{ overflow: 'hidden', padding: 2 }}>
                              <Stack spacing={2} direction="column">
                                 <Skeleton variant="rounded" height={155} width="100%" />
                                 <Skeleton variant="text" height={14} width="90%" />
                                 <Skeleton variant="text" height={14} width="60%" />
                              </Stack>
                           </Paper>
                        </Grid>
                     ))}
                  </Grid>
               </Box>
            )}

            {relatedTemplates?.data && (
               <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={{ xs: 4, md: 8, lg: 16 }} columns={{ xs: 4, sm: 8, md: 12, lg: 16 }}>
                     {relatedTemplates?.data?.map((template) => (
                        <Grid item xs={4} sm={4} md={4} lg={4} key={template.id}>
                           <TemplatesCard template={template} />
                        </Grid>
                     ))}
                  </Grid>
               </Box>
            )}
         </Stack>
      );
   }
   return null;
}
